/**
 * Lightweight heuristics for PDF-like wrapping, lists, and table-ish lines.
 * Pure functions — no DOM.
 */

const LIST_LINE_RE =
  /^\s*(?:[-*•‣▪▸►◦○●]|\d{1,3}[.)]\s|\([a-zA-Z]\)\s|[a-zA-Z][.)]\s)/;

const SENTENCE_END_RE = /[.!?:)"'\u201d\u2019]\s*$/;

/** @param {string} line */
export function isListLine(line) {
  return LIST_LINE_RE.test(line.trimStart());
}

/** @param {string} line */
export function isTableLikeLine(line) {
  if (line.includes('\t')) {
    const tabs = (line.match(/\t/g) || []).length;
    if (tabs >= 1) return true;
  }
  // Column-ish spacing: multiple internal gaps of 2+ spaces
  return /\S\s{2,}\S\s{2,}\S/.test(line);
}

/**
 * @param {string[]} lines non-empty trimmed count for stats
 */
export function detectPdfLike(lines) {
  const nonEmpty = lines.map((l) => l.trim()).filter(Boolean);
  // Need several wrapped lines to look PDF-like; <3 avoids merging unrelated pairs (e.g. after dedupe).
  if (nonEmpty.length < 3) return false;

  const lengths = nonEmpty.map((l) => l.length);
  const avg = lengths.reduce((a, b) => a + b, 0) / lengths.length;
  if (avg >= 62) return false;

  let noSentenceEnd = 0;
  for (const l of nonEmpty) {
    if (!SENTENCE_END_RE.test(l) && !isListLine(l)) noSentenceEnd += 1;
  }
  const ratio = noSentenceEnd / nonEmpty.length;
  return ratio >= 0.38;
}

/**
 * @param {string[]} lines
 */
export function detectHasLists(lines) {
  let listCount = 0;
  let n = 0;
  for (const l of lines) {
    const t = l.trim();
    if (!t) continue;
    n += 1;
    if (isListLine(l)) listCount += 1;
  }
  return n > 0 && listCount / n >= 0.25;
}

/**
 * @param {string[]} lines
 */
export function detectTableLikeBlock(lines) {
  const nonEmpty = lines.filter((l) => l.trim());
  if (nonEmpty.length < 3) return false;
  let tabley = 0;
  for (const l of nonEmpty) {
    if (isTableLikeLine(l)) tabley += 1;
  }
  return tabley / nonEmpty.length >= 0.35;
}

/**
 * @param {string} text
 */
export function analyzeText(text) {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  return {
    pdfLike: detectPdfLike(lines),
    hasLists: detectHasLists(lines),
    tableLike: detectTableLikeBlock(lines),
    lineCount: lines.length,
  };
}
